import React, { useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { logUiEvent } from '../services/uiLogger';
import keycloak, { keycloakEnabled } from '../keycloak';
import { CLIENT_HOST, CLIENT_URL } from '../lib/host';
import { emailLayoutError } from '../lib/emailLayout';

// Контролы входа сотрудника. Карточку, логотип и заголовок рисует StaffEntryPage —
// здесь только то, чем человек действует. Верстка по «Бумаге»
// (docs/redesign/2026-09-04-bumaga/, артборд «Вход · сотрудники»).
//
// 🪤 Что есть здесь и чего НЕТ в макете: «Запросить токен» и «Зарегистрироваться».
// Макет рисует только SSO и вход по готовому токену из письма. Оба легаси-пути
// оставлены намеренно до отдельного решения владельца: инстанс без Keycloak
// (`keycloakEnabled === false`) живёт только на них, и выкинуть их значит запереть
// такую установку. Убирать — отдельным изменением, а не заодно с покраской.

const PRIMARY = 'w-full rounded-control py-[14px] text-[15px] font-semibold '
  + 'bg-paper-accent text-white hover:bg-paper-accent-hover disabled:bg-paper-bg '
  + 'disabled:text-paper-hint transition-colors';

const QUIET = 'text-[12px] text-paper-ink-3 underline underline-offset-[3px] hover:text-paper-ink';

const FIELD = 'w-full rounded-[10px] bg-paper-bg px-3 py-[10px] text-[13px] '
  + 'text-paper-ink placeholder:text-paper-hint outline-none';

const AuthPage = ({ onAuth }) => {
  const AUTH_MODES = {
    REQUEST_TOKEN: 'request-token',
    ENTER_TOKEN: 'enter-token',
    REGISTER: 'register',
    RESET_PASSWORD: 'reset-password',
  };

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [authMode, setAuthMode] = useState(AUTH_MODES.ENTER_TOKEN);
  const [consent, setConsent] = useState(false);
  // Под KC вход по токену скрыт за ссылкой (вторичный); без KC — показан сразу.
  const [showTokenLogin, setShowTokenLogin] = useState(!keycloakEnabled);
  // Уход в AI Platform: браузер переходит не мгновенно, и без этого состояния
  // экран секунду выглядит так, будто нажатие не сработало.
  const [ssoPending, setSsoPending] = useState(false);
  // Что предложить, когда учётки в AI Platform нет: 'token' — вход по токену
  // (аккаунт Ньютона есть, он просто старый), 'register' — завести учётку.
  const [resetNextStep, setResetNextStep] = useState('');
  // Почему прошлый вход через AI Platform не удался (App кладёт перед logout).
  const [ssoNotice] = useState(() => authService.peekLoginNotice());
  useEffect(() => { authService.clearLoginNotice(); }, []);

  const handleRequestToken = async () => {
    setError('');
    setMessage('');
    const layout = emailLayoutError(email);
    if (layout) { setError(layout); return; }
    try {
      const response = await authService.requestToken(email);
      setMessage(response.message);
      setAuthMode(AUTH_MODES.ENTER_TOKEN);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmitToken = async () => {
    setError('');
    setMessage('');
    try {
      await onAuth(token);
    } catch (err) {
      setError(err.message || 'Ошибка при проверке токена');
    }
  };

  const cancelRequestToken = () => {
    setError('');
    setMessage('');
    setAuthMode(AUTH_MODES.ENTER_TOKEN);
  };

  const handleRegister = async () => {
    setError('');
    setMessage('');
    const layout = emailLayoutError(email);
    if (layout) { setError(layout); return; }
    try {
      const response = await authService.register(email);
      setMessage(response.message);
      setEmail('');
      setConsent(false);
      setAuthMode(AUTH_MODES.ENTER_TOKEN);
    } catch (err) {
      // 403 по домену здесь значит не «нельзя», а «вы не сотрудник». Своей
      // формы регистрации у клиента тут больше нет — он уходит на свой домен:
      // ключ доступа, выданный на ys, в localStorage newton всё равно не
      // попадёт, и человек оказался бы незалогиненным по нашим же ссылкам.
      if (err.code === 'domain_not_allowed') {
        setError('');
        setMessage(CLIENT_HOST
          ? `Это регистрация для сотрудников. Вход для клиентов — на ${CLIENT_HOST}.`
          : 'Это регистрация для сотрудников. Вход для клиентов — на клиентском домене.');
        logUiEvent('client_redirected_from_register', { component: 'auth' });
        return;
      }
      setError(err.message);
    }
  };

  const handlePasswordReset = async () => {
    setError('');
    setMessage('');
    setResetNextStep('');
    const layout = emailLayoutError(email);
    if (layout) { setError(layout); return; }
    try {
      const response = await authService.requestPasswordReset(email);
      // Остаёмся на этом же экране: подтверждение «письмо ушло» человек должен
      // увидеть, а на карточке входа его негде показать.
      setMessage(response.message);
    } catch (err) {
      // Главное отличие от Keycloak: «учётки нет» здесь не молчание, а ответ,
      // и у ответа есть продолжение. У кого аккаунт Ньютона старый (в Keycloak
      // его нет вовсе — таких больше половины), тому нужен вход по токену, а не
      // регистрация; у кого нет и его — регистрация.
      if (err.code === 'user_not_found') {
        setError('');
        setMessage(err.message);
        // Экран не переключаем: человек должен дочитать ответ. Следующий шаг
        // стоит кнопкой под текстом — он разный, и молча увести не туда хуже,
        // чем ничего не сделать.
        setResetNextStep(err.hasLegacyAccount ? 'token' : 'register');
        logUiEvent('password_reset_no_account', {
          component: 'auth', legacy: err.hasLegacyAccount ? 1 : 0,
        });
        return;
      }
      setError(err.message);
    }
  };

  const errorLine = (id, text) => (
    <p id={id} className="text-[12px] leading-[1.5] text-paper-accent">{text}</p>
  );

  // Уход в AI Platform. Отмена возвращает карточку в исходное состояние: если
  // человек передумал или окно провайдера не открылось, экран не заперт.
  const renderSsoPending = () => (
    <div className="flex flex-col items-center gap-[14px] py-5 text-center">
      <span className="w-[10px] h-[10px] rounded-full bg-paper-accent" />
      <div className="text-[15px] font-semibold text-paper-ink">Переходим в AI Platform…</div>
      <div className="text-[13px] leading-[1.5] text-paper-ink-2 max-w-[30ch]">
        Вернёмся сюда сами.
      </div>
      <button type="button" className={QUIET} onClick={() => setSsoPending(false)}>Отмена</button>
    </div>
  );

  const renderTokenLogin = () => (
    <div className="border-t border-paper-bg pt-[14px] flex flex-col gap-2">
      <div className="text-[12px] text-paper-ink-3">Есть токен доступа из письма?</div>
      <div className="flex gap-[6px]">
        <input
          id="input-token"
          type="text"
          placeholder="nwt_…"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className={`${FIELD} flex-1 min-w-0 font-mono`}
        />
        <button
          id="button-submit-token"
          type="button"
          disabled={!token}
          onClick={handleSubmitToken}
          className="rounded-[10px] px-[14px] py-[10px] text-[13px] font-semibold bg-paper-ink text-white disabled:bg-paper-bg disabled:text-paper-hint transition-colors"
        >
          Войти
        </button>
      </div>
      {error && errorLine('error-token', error)}
      {message && <p id="message-token" className="text-[12px] text-paper-success">{message}</p>}
      <button
        id="button-switch-to-request"
        type="button"
        className={`${QUIET} self-start`}
        onClick={() => setAuthMode(AUTH_MODES.REQUEST_TOKEN)}
      >
        Токена нет — запросить
      </button>
    </div>
  );

  const renderRequestTokenView = () => (
    <div className="border-t border-paper-bg pt-[14px] flex flex-col gap-2">
      <div className="text-[12px] text-paper-ink-3">Пришлём токен доступа на почту</div>
      <input
        id="input-email"
        type="email"
        placeholder="you@1cbit.ru"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={FIELD}
      />
      {error && errorLine('error-email', error)}
      <div className="flex gap-[6px]">
        <button
          id="button-request-token"
          type="button"
          disabled={!email}
          onClick={handleRequestToken}
          className="flex-1 rounded-[10px] px-[14px] py-[10px] text-[13px] font-semibold bg-paper-ink text-white disabled:bg-paper-bg disabled:text-paper-hint transition-colors"
        >
          Запросить токен
        </button>
        <button
          id="button-cancel-request"
          type="button"
          onClick={cancelRequestToken}
          className="rounded-[10px] px-[14px] py-[10px] text-[13px] font-semibold bg-paper-bg text-paper-ink"
        >
          Отмена
        </button>
      </div>
    </div>
  );

  // «Забыли пароль?» ведёт сюда, а не на страницу Keycloak: та отвечает
  // одинаково и знакомому адресу, и незнакомому, поэтому человек без учётки
  // ждёт письмо, которого не будет. Здесь ответ прямой.
  const renderResetPasswordView = () => (
    <div className="flex flex-col gap-[14px]">
      <div className="flex flex-col gap-2">
        <label htmlFor="input-reset-email" className="text-[12px] text-paper-ink-3">
          Рабочая почта
        </label>
        <input
          id="input-reset-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={FIELD}
        />
        {error && errorLine('error-reset', error)}
        {message && (
          <p data-testid="message-reset" className="text-[13px] leading-[1.5] text-paper-ink-2">
            {message}
          </p>
        )}
      </div>

      {resetNextStep === 'register' ? (
        <button
          id="button-reset-goto-register"
          type="button"
          className={PRIMARY}
          onClick={() => {
            setError('');
            setMessage('');
            setResetNextStep('');
            setAuthMode(AUTH_MODES.REGISTER);
          }}
        >
          Зарегистрироваться
        </button>
      ) : resetNextStep === 'token' ? (
        <button
          id="button-reset-goto-token"
          type="button"
          className={PRIMARY}
          onClick={() => {
            setError('');
            setMessage('');
            setResetNextStep('');
            setShowTokenLogin(true);
            setAuthMode(AUTH_MODES.REQUEST_TOKEN);
          }}
        >
          Получить токен на почту
        </button>
      ) : (
        <button
          id="button-reset-password"
          type="button"
          className={PRIMARY}
          disabled={!email}
          onClick={handlePasswordReset}
        >
          Прислать письмо
        </button>
      )}

      <p className="text-center text-[12px] text-paper-ink-3">
        <button
          id="button-back-to-login"
          type="button"
          className="font-semibold text-paper-ink hover:text-paper-accent"
          onClick={() => { setError(''); setMessage(''); setAuthMode(AUTH_MODES.ENTER_TOKEN); }}
        >
          Назад ко входу
        </button>
      </p>
    </div>
  );

  const renderRegisterView = () => (
    <div className="flex flex-col gap-[14px]">
      <div className="flex flex-col gap-2">
        <label htmlFor="input-register-email" className="text-[12px] text-paper-ink-3">
          Рабочая почта
        </label>
        <input
          id="input-register-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={FIELD}
        />
        {error && errorLine('error-register', error)}
        {message && (
          <p data-testid="message-register" className="text-[13px] leading-[1.5] text-paper-ink-2">
            {message}{' '}
            {/* Без клиентского домена ссылки нет: увести человека на чужой боевой
                контур хуже, чем не показать ссылку вовсе. */}
            {CLIENT_URL && (
              <a href={CLIENT_URL} className="font-semibold text-paper-accent hover:underline">
                Перейти
              </a>
            )}
          </p>
        )}
      </div>

      <label className="flex items-start gap-3 text-[12px] leading-[1.5] text-paper-ink-2 cursor-pointer">
        <input
          id="input-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-[2px] accent-paper-accent w-4 h-4"
        />
        <span>
          Я соглашаюсь с{' '}
          <a href="/oferta" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">офертой</a>{' '}
          и{' '}
          <a href="/consent" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">
            согласием на обработку персональных данных</a>.
        </span>
      </label>

      <button
        id="button-register"
        type="button"
        className={PRIMARY}
        disabled={!email || !consent}
        onClick={handleRegister}
      >
        Зарегистрироваться
      </button>

      <p className="text-center text-[12px] text-paper-ink-3">
        Уже есть аккаунт?{' '}
        <button
          id="button-switch-to-login"
          type="button"
          className="font-semibold text-paper-ink hover:text-paper-accent"
          onClick={() => { setError(''); setMessage(''); setAuthMode(AUTH_MODES.ENTER_TOKEN); }}
        >
          Войти
        </button>
      </p>
    </div>
  );

  if (ssoPending) return renderSsoPending();
  if (authMode === AUTH_MODES.REGISTER) return renderRegisterView();
  if (authMode === AUTH_MODES.RESET_PASSWORD) return renderResetPasswordView();

  return (
    <div className="flex flex-col gap-[18px]">
      {keycloakEnabled && (
        <>
          <button
            id="button-keycloak-login"
            type="button"
            className={PRIMARY}
            onClick={() => { setSsoPending(true); keycloak.login(); }}
          >
            Войти через AI Platform
          </button>
          <div className="text-[12px] leading-[1.5] text-paper-ink-3">
            Корпоративная учётка @1cbit.ru.
          </div>
          {ssoNotice && errorLine('error-sso', ssoNotice)}
          <button
            id="button-goto-reset"
            type="button"
            className={`${QUIET} self-start`}
            onClick={() => { setError(''); setMessage(''); setAuthMode(AUTH_MODES.RESET_PASSWORD); }}
          >
            Забыли пароль?
          </button>
        </>
      )}

      {(!keycloakEnabled || showTokenLogin)
        ? (authMode === AUTH_MODES.REQUEST_TOKEN ? renderRequestTokenView() : renderTokenLogin())
        : (
          <button
            type="button"
            className={`${QUIET} self-start`}
            onClick={() => setShowTokenLogin(true)}
          >
            Есть токен доступа из письма?
          </button>
        )}

      <button
        id="button-goto-register"
        type="button"
        className={`${QUIET} self-start`}
        onClick={() => { setError(''); setMessage(''); setAuthMode(AUTH_MODES.REGISTER); }}
      >
        Зарегистрироваться
      </button>
    </div>
  );
};

export default AuthPage;
